import { useEffect, useRef, useState } from "react";
import {
  TrendingUp, Shield, LogIn, User, ChevronDown, LogOut, LayoutDashboard,
  Image as ImageIcon, Bot, Wrench, Eye, GitCompare, BarChart3, Zap,
  BookOpen, Download, ShoppingCart, Gift, Sparkles, CreditCard, Brain, AlertTriangle,
  MessageSquare, ShoppingBag, Mail, FileText, ShieldCheck, Trophy,
  Menu, X,
} from "lucide-react";
import { LanguageSelector } from "../components/common/LanguageSelector";
import { NotificationBell } from "../components/common/NotificationBell";
import { useAuth } from "../contexts/AuthContext";

/**
 * LandingHeader v2 — engaging top nav with:
 *   · "Products" mega-dropdown (categorised) so prospects can discover the
 *     full portfolio without scrolling to the footer.
 *   · Post-login User menu: avatar, plan badge, 5 shortcuts + sticky Upgrade
 *     CTA for non-Enterprise users (drives conversion).
 *   · Mobile: slide-down full-menu with the same data.
 */
import ScarcityBanner from "../components/marketing/ScarcityBanner";

export function LandingHeader({ t, user, language, setLanguage, languages, onOpenAdmin }) {
  return (
    <header
      className="fixed top-0 left-0 right-0 backdrop-blur-xl border-b z-50"
      style={{ background: "color-mix(in srgb, var(--color-bg) 80%, transparent)", borderColor: "var(--color-border)" }}
      data-testid="header"
    >
      {/* V223 — FOMO scarcity banner. Rendered as first element inside the
          fixed header so it's always visible at the top and never gets
          covered by the header itself. Self-hides on dismiss + cooldown. */}
      <ScarcityBanner language={language} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2" data-testid="nav-logo">
          <div className="w-7 h-7 bg-[#a855f7] flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-white" />
          </div>
          <span className="text-lg font-black font-['Cabinet_Grotesk'] tracking-tight">TopRankPulse</span>
        </a>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-4">
          <ProductsDropdown t={t} />
          <a href="/sluzby" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors font-['JetBrains_Mono'] uppercase" data-testid="nav-sluzby">
            {t.nav_services || "Služby"}
          </a>
          <AnchorLink href="/#pricing" testid="nav-pricing">{t.nav_pricing || "Pricing"}</AnchorLink>
          <AnchorLink href="/#seo-tools" testid="nav-seo-tools">{t.nav_tools || "Tools"}</AnchorLink>
          <a href="/blog" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors font-['JetBrains_Mono'] uppercase" data-testid="nav-blog">
            {t.nav_blog || "Blog"}
          </a>
          {user?.role === "admin" && (
            <button onClick={onOpenAdmin} className="text-[var(--color-text-muted)] hover:text-[#a855f7]" data-testid="nav-admin" title="Admin">
              <Shield className="w-4 h-4" />
            </button>
          )}
          {/* V165.2 fix: hide notification bell from public visitors.
              Daily Recap / Lead alerts are internal owner notifications and must
              not leak to anonymous clients. Show only for signed-in users
              who own the dashboard (admins + paying customers). */}
          {user && <NotificationBell variant="dark" />}
          {user ? (
            <UserMenu t={t} user={user} />
          ) : (
            <a
              href="/login"
              className="flex items-center gap-2 px-4 py-2 bg-[#a855f7] text-white text-sm font-bold hover:bg-purple-600 transition-colors"
              data-testid="nav-login"
            >
              <LogIn className="w-4 h-4" />
              {t.nav_sign_in || "Sign In"}
            </a>
          )}
          <LanguageSelector currentLang={language} onChangeLang={setLanguage} languages={languages} />
        </div>

        {/* Mobile nav */}
        <div className="flex lg:hidden items-center gap-2">
          {user ? (
            <UserMenu t={t} user={user} compact />
          ) : (
            <a href="/login" className="px-3 py-1.5 bg-[#a855f7] text-white text-xs font-bold" data-testid="nav-login-mobile">
              {t.nav_sign_in || "Sign In"}
            </a>
          )}
          <LanguageSelector currentLang={language} onChangeLang={setLanguage} languages={languages} />
          <MobileMenu t={t} />
        </div>
      </div>
    </header>
  );
}

/* ---------------- Anchor link with same-page scroll ---------------- */

function AnchorLink({ href, children, testid }) {
  const onClick = (e) => {
    // href is like "/#pricing". If we're already on the landing root, scroll
    // smoothly instead of triggering a full page navigation that the router
    // would otherwise swallow.
    if (typeof window === "undefined") return;
    const hashIdx = href.indexOf("#");
    if (hashIdx < 0) return;
    const id = href.slice(hashIdx + 1);
    if (window.location.pathname === "/" || window.location.pathname === "") {
      const el = document.getElementById(id);
      if (el) {
        e.preventDefault();
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.replaceState(null, "", href);
      }
    }
  };
  return (
    <a
      href={href}
      onClick={onClick}
      data-testid={testid}
      className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors font-['JetBrains_Mono'] uppercase"
    >
      {children}
    </a>
  );
}

/* ---------------- Products mega-dropdown ---------------- */

function productGroups(t) {
  return [
    {
      label: t.nav_prod_agents || "🤖 AI Agents · na prenájom",
      items: [
        { icon: MessageSquare, href: "/agent-os/demo/helpdesk", title: "Helpdesk Agent", sub: t.nav_p_help_sub || "Branded odpovede na support tickety, klasifikácia + escalation", badge: "€99/mo" },
        { icon: MessageSquare, href: "/agent-os/demo/review-reply", title: "Review Reply Agent", sub: t.nav_p_review_sub || "Auto-odpovedá na Google/Heureka/Trustpilot recenzie", badge: "€79/mo" },
        { icon: ShoppingBag, href: "/agent-os/demo/cart-recovery", title: "Cart Recovery Agent", sub: t.nav_p_cart_sub || "Personalizovaný recovery email flow (12-18% rate)", badge: "€99/mo" },
        { icon: FileText, href: "/agent-os/demo/product-desc", title: "Product Desc Optimizer", sub: t.nav_p_pd_sub || "Bulk prepíše popisy pre SEO + AI Search", badge: "€79/mo" },
        { icon: Mail, href: "/agent-os/demo/newsletter", title: "Newsletter Agent", sub: t.nav_p_nl_sub || "Týždenné AI newslettery z tvojho katalógu", badge: "€79/mo" },
        { icon: ShieldCheck, href: "/agent-os/demo/gdpr-watch", title: "GDPR + A11y Watch", sub: t.nav_p_gdpr_sub || "Denný compliance scan: cookies, WCAG, alt texty", badge: "€49/mo" },
        { icon: Bot, href: "/agent-os/eshop", title: t.nav_p_marketplace || "Marketplace →", sub: t.nav_p_marketplace_sub || "Všetkých 6 agentov · Bundle 3-za-2 · 14-day trial", badge: "TRIAL" },
        { icon: Trophy, href: "/agent-os/leaderboard", title: t.nav_p_lb || "Top Referrers", sub: t.nav_p_lb_sub || "Kto najviac zarába zdieľaním", badge: "NEW" },
      ],
    },
    {
      label: t.nav_prod_free || "Free",
      items: [
        { icon: Zap, href: "/", title: t.nav_p_scan || "AI SEO Scan", sub: t.nav_p_scan_sub || "Instant visibility scan", badge: "FREE" },
        { icon: Sparkles, href: "/free-ads-preview", title: t.nav_p_adspv || "AI Ads Preview", sub: t.nav_p_adspv_sub || "Preview 3 Google Ad variants in 10s", badge: "FREE" },
        { icon: Bot, href: "/ai-simulator", title: t.nav_p_sim || "AI Simulator", sub: t.nav_p_sim_sub || "See how ChatGPT answers about you", badge: "FREE" },
        { icon: Shield, href: "/cookie-audit", title: t.nav_p_ca || "Cookie / GDPR Audit", sub: t.nav_p_ca_sub || "Compliance health check", badge: "FREE" },
      ],
    },
    {
      label: t.nav_prod_paid || "Paid — monthly",
      items: [
        { icon: Brain, href: "/ai-intelligence", title: "AI Intelligence Bundle", sub: t.nav_p_aii_sub || "Prompts · Citations · Sentiment · Gaps", badge: "€39/mo" },
        { icon: Eye, href: "/brand-monitor", title: "Brand Monitor", sub: t.nav_p_bm_sub || "Track brand across AI engines", badge: "€19/mo" },
        { icon: GitCompare, href: "/competitor-spy", title: "Competitor Spy", sub: t.nav_p_cs_sub || "Watch what competitors rank for", badge: "€29/mo" },
        { icon: BarChart3, href: "/attribution", title: "AI Attribution", sub: t.nav_p_at_sub || "Know which AI drives sales", badge: "€39/mo" },
        { icon: Zap, href: "/seo-autopilot", title: "SEO Autopilot", sub: t.nav_p_ap_sub || "Auto-fix SEO issues 24/7", badge: "€79/mo" },
      ],
    },
    {
      label: t.nav_prod_studio || "Studio",
      items: [
        { icon: ImageIcon, href: "/ads-creator", title: "PulseStudio", sub: t.nav_p_ads_sub || "Ads, Reels, Photo → AI", badge: "€69/mo" },
        { icon: Sparkles, href: "/editor", title: t.nav_p_editor || "Editor showcase", sub: t.nav_p_editor_sub || "See real ads built with the editor", badge: "FREE" },
        { icon: BookOpen, href: "/blog-agent", title: "Blog Agent", sub: t.nav_p_ba_sub || "Autopilot blog writing", badge: "€25" },
        { icon: Wrench, href: "/fix-my-seo", title: "Fix My SEO", sub: t.nav_p_fx_sub || "One-time pro audit + fixes", badge: "€9" },
        { icon: AlertTriangle, href: "/revenue-leak", title: t.nav_p_rl || "Revenue Leak Audit", sub: t.nav_p_rl_sub || "Find 10 conversion leaks + 5-day plan", badge: "€47" },
      ],
    },
    {
      label: t.nav_prod_plugins || "Plugins & Integrations",
      items: [
        { icon: Download, href: "/plugin", title: "WordPress", sub: "1-click install", badge: "FREE" },
        { icon: ShoppingCart, href: "/plugin#woocommerce", title: "WooCommerce", sub: "Embed scan on shop", badge: "FREE" },
        { icon: ShoppingCart, href: "/plugin#shopify", title: "Shopify", sub: "Embed scan on store", badge: "FREE" },
      ],
    },
  ];
}

function ProductsDropdown({ t }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const k = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", h);
    document.addEventListener("keydown", k);
    return () => { document.removeEventListener("mousedown", h); document.removeEventListener("keydown", k); };
  }, [open]);
  const groups = productGroups(t);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors font-['JetBrains_Mono'] uppercase"
        data-testid="nav-products"
      >
        {t.nav_products || "Products"}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="nav-products-panel"
          className="absolute right-0 top-full mt-3 p-5 rounded-xl border shadow-2xl z-50"
          style={{
            background: "var(--color-bg)",
            borderColor: "var(--color-border)",
            width: "min(840px, calc(100vw - 48px))",
            maxHeight: "calc(100vh - 120px)",
            overflow: "auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 24,
          }}
        >
          {groups.map((g) => (
            <div key={g.label}>
              <div className="text-[11px] font-['JetBrains_Mono'] uppercase tracking-wider text-[#c084fc] mb-3">{g.label}</div>
              <div className="space-y-1">
                {g.items.map((it) => (
                  <ProductLink key={it.href} {...it} onClick={() => setOpen(false)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductLink({ icon: Icon, href, title, sub, badge, onClick }) {
  const isFree = badge === "FREE";
  return (
    <a
      href={href}
      onClick={onClick}
      className="flex items-start gap-3 p-2.5 rounded-lg transition-colors group"
      style={{ color: "var(--color-text)" }}
      onMouseEnter={(e) => e.currentTarget.style.background = "rgba(168,85,247,0.08)"}
      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
    >
      <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 rounded-lg" style={{ background: "rgba(168,85,247,0.12)" }}>
        <Icon className="w-4 h-4" style={{ color: "#c084fc" }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-bold text-sm">{title}</span>
          <span className={`px-1.5 py-0.5 text-[9px] font-black rounded ${isFree ? "bg-emerald-500/15 text-emerald-400" : "bg-[#a855f7]/15 text-[#c084fc]"}`}>
            {badge}
          </span>
        </div>
        <div className="text-xs mt-0.5" style={{ color: "var(--color-text-muted)" }}>{sub}</div>
      </div>
    </a>
  );
}

/* V160.2 — Mobile-safe product link with HARDCODED colors (no CSS vars).
   Samsung Browser sometimes fails to resolve CSS vars on lazy-loaded
   themed pages → text became invisible (white on white). Hardcoded
   colors guarantee visibility regardless of theme initialization race. */
function MobileProductLink({ icon: Icon, href, title, sub, badge, onClick }) {
  const isFree = badge === "FREE";
  return (
    <a
      href={href}
      onClick={onClick}
      className="flex items-start gap-3 p-2.5 rounded-lg"
      style={{ color: "#fafafa", textDecoration: "none", background: "transparent" }}
      data-testid={`mobile-product-link-${(href || "").replace(/[^a-zA-Z0-9]/g, "-").toLowerCase().slice(0, 40)}`}
    >
      <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 rounded-lg" style={{ background: "rgba(168,85,247,0.18)" }}>
        <Icon className="w-4 h-4" style={{ color: "#c084fc" }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-sm" style={{ color: "#fafafa" }}>{title}</span>
          {badge && (
            <span
              className="px-1.5 py-0.5 text-[9px] font-black rounded"
              style={{
                background: isFree ? "rgba(16,185,129,0.18)" : "rgba(168,85,247,0.18)",
                color: isFree ? "#34d399" : "#c084fc",
              }}
            >
              {badge}
            </span>
          )}
        </div>
        <div className="text-xs mt-0.5" style={{ color: "#a1a1aa" }}>{sub}</div>
      </div>
    </a>
  );
}

/* ---------------- User menu ---------------- */

function UserMenu({ t, user, compact }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { logout } = useAuth();
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const k = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", h);
    document.addEventListener("keydown", k);
    return () => { document.removeEventListener("mousedown", h); document.removeEventListener("keydown", k); };
  }, [open]);

  const initial = (user?.email || "U").trim().charAt(0).toUpperCase();
  const plan = (user?.plan || "free").toLowerCase();
  const showUpgrade = plan === "free" || plan === "starter";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={compact ? "nav-user-compact" : "nav-user"}
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-[#a855f7]/10 transition-colors"
      >
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#a855f7] to-[#7c3aed] flex items-center justify-center text-white text-xs font-black">
          {initial}
        </div>
        {!compact && (
          <>
            <span className="text-[11px] font-['JetBrains_Mono'] uppercase px-1.5 py-0.5 rounded bg-[#a855f7]/15 text-[#c084fc]" data-testid="nav-user-plan">
              {plan}
            </span>
            <ChevronDown className={`w-3.5 h-3.5 text-[var(--color-text-muted)] transition-transform ${open ? "rotate-180" : ""}`} />
          </>
        )}
      </button>

      {open && (
        <div
          data-testid="nav-user-menu"
          className="absolute right-0 top-full mt-2 rounded-xl border shadow-2xl z-50 overflow-hidden"
          style={{
            background: "var(--color-bg)",
            borderColor: "var(--color-border)",
            width: 300,
          }}
        >
          {/* Identity header */}
          <div className="px-4 pt-4 pb-3 border-b" style={{ borderColor: "var(--color-border)" }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#a855f7] to-[#7c3aed] flex items-center justify-center text-white font-black">
                {initial}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold truncate" style={{ color: "var(--color-text)" }}>{user?.email}</div>
                <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider" style={{ color: "var(--color-text-muted)" }}>
                  {plan} {t.nav_user_plan_suffix || "plan"}
                </div>
              </div>
            </div>
          </div>

          {/* Upgrade CTA (free/starter only) */}
          {showUpgrade && (
            <a
              href="/#pricing"
              onClick={() => setOpen(false)}
              data-testid="nav-user-upgrade"
              className="block mx-3 my-3 p-3 rounded-lg text-left transition-transform hover:scale-[1.02]"
              style={{ background: "linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)" }}
            >
              <div className="flex items-center gap-2 text-white">
                <Sparkles className="w-4 h-4" />
                <span className="font-black text-sm">{t.nav_user_upgrade_title || "Unlock Pro features"}</span>
              </div>
              <div className="text-xs text-white/85 mt-1">
                {t.nav_user_upgrade_sub || "AI Monitor, Competitor Spy, Autopilot + more. From €19/mo."}
              </div>
            </a>
          )}

          {/* Quick links */}
          <div className="py-1">
            <MenuLink icon={LayoutDashboard} href="/dashboard" label={t.nav_user_dashboard || "Dashboard"} onClick={() => setOpen(false)} testid="nav-user-dashboard" />
            <MenuLink icon={ImageIcon} href="/ads-creator/my-ads" label={t.nav_user_my_ads || "My Ads (PulseStudio)"} onClick={() => setOpen(false)} />
            <MenuLink icon={Zap} href="/" label={t.nav_user_scan || "New SEO Scan"} onClick={() => setOpen(false)} badge="FREE" badgeColor="text-emerald-400 bg-emerald-500/15" />
            <MenuLink icon={Bot} href="/ai-simulator" label={t.nav_user_sim || "AI Simulator"} onClick={() => setOpen(false)} badge="FREE" badgeColor="text-emerald-400 bg-emerald-500/15" />
            <MenuLink icon={Gift} href="/community/top-sharers" label={t.nav_user_referral || "Earn free credits"} onClick={() => setOpen(false)} />
            <MenuLink icon={CreditCard} href="/dashboard?tab=addons" label={t.nav_user_billing || "Billing & Add-ons"} onClick={() => setOpen(false)} />
          </div>

          {/* Sign out */}
          <div className="border-t py-1" style={{ borderColor: "var(--color-border)" }}>
            <button
              type="button"
              onClick={() => { setOpen(false); logout(); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
              data-testid="nav-user-logout"
            >
              <LogOut className="w-4 h-4" />
              {t.nav_user_logout || "Sign out"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MenuLink({ icon: Icon, href, label, onClick, testid, badge, badgeColor }) {
  return (
    <a
      href={href}
      onClick={onClick}
      data-testid={testid}
      className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-[#a855f7]/10"
      style={{ color: "var(--color-text)" }}
    >
      <Icon className="w-4 h-4" style={{ color: "var(--color-text-muted)" }} />
      <span className="flex-1">{label}</span>
      {badge && (
        <span className={`px-1.5 py-0.5 text-[9px] font-black rounded ${badgeColor || "text-[#c084fc] bg-[#a855f7]/15"}`}>
          {badge}
        </span>
      )}
    </a>
  );
}

/* ---------------- Mobile menu ---------------- */

function MobileMenu({ t }) {
  const [open, setOpen] = useState(false);
  const groups = productGroups(t);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="p-2"
        style={{ color: "#fafafa" }}
        data-testid="mobile-menu-btn"
        aria-label="menu"
      >
        <Menu className="w-6 h-6" />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-[60]"
            data-testid="mobile-menu-overlay"
            onClick={() => setOpen(false)}
            style={{ background: "rgba(0,0,0,0.75)" }}
          />
          <div
            onClick={(e) => e.stopPropagation()}
            className="fixed z-[61] overflow-y-auto"
            style={{
              top: 0,
              right: 0,
              bottom: 0,
              height: "100vh",
              width: "min(360px, 86vw)",
              background: "#0a0a0a",
              borderLeft: "1px solid #27272a",
              boxShadow: "-8px 0 32px rgba(0,0,0,0.6)",
              color: "#fafafa",
              WebkitOverflowScrolling: "touch",
            }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid #27272a" }}>
              <span className="font-black font-['Cabinet_Grotesk'] text-lg" style={{ color: "#fafafa" }}>{t.nav_mobile_title || "Menu"}</span>
              <button onClick={() => setOpen(false)} aria-label="close" className="p-1" style={{ color: "#fafafa" }}><X className="w-5 h-5" /></button>
            </div>
            <div className="px-4 py-4 space-y-3" style={{ color: "#fafafa" }}>
              <a href="/#pricing" className="block py-2 text-sm font-['JetBrains_Mono'] uppercase" style={{ color: "#fafafa", borderBottom: "1px solid #27272a" }} onClick={() => setOpen(false)} data-testid="mobile-menu-pricing">{t.nav_pricing || "Pricing"}</a>
              <a href="/#seo-tools" className="block py-2 text-sm font-['JetBrains_Mono'] uppercase" style={{ color: "#fafafa", borderBottom: "1px solid #27272a" }} onClick={() => setOpen(false)} data-testid="mobile-menu-tools">{t.nav_tools || "Tools"}</a>
              <a href="/blog" className="block py-2 text-sm font-['JetBrains_Mono'] uppercase" style={{ color: "#fafafa", borderBottom: "1px solid #27272a" }} onClick={() => setOpen(false)} data-testid="mobile-menu-blog">{t.nav_blog || "Blog"}</a>
              {groups.length === 0 && (
                <div className="text-xs py-4" style={{ color: "#71717a" }} data-testid="mobile-menu-empty-warn">
                  {t.nav_no_items || "Žiadne produkty k zobrazeniu. Skús obnoviť stránku."}
                </div>
              )}
              {groups.map((g) => (
                <div key={g.label} data-testid={`mobile-menu-group-${g.label.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`} className="pt-3">
                  <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider mb-2" style={{ color: "#c084fc" }}>{g.label}</div>
                  <div className="space-y-1">
                    {g.items.map((it) => (
                      <MobileProductLink key={it.href} {...it} onClick={() => setOpen(false)} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}
